import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CatalogService from "../../services/catalog.service";
import OrderService from "../../services/order.service";
import Carousel from "react-bootstrap/Carousel";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import AnimateHeight from "react-animate-height";
import ChooseAddress from "../ChooseAddress";
import AddressForm from "../modals/AddressForm";

import ButtonGroup from "react-bootstrap/ButtonGroup";
import { selectCurrentNewHireKitItems, getNewHireKitItems, selectDecorations, selectInventory } from '../../reducers/catalog';
import { addItemToOrder, cartCount, toggleShowCart, cart, updateItemQuantity } from '../../reducers/cart';
import { isLoggedIn } from '../../reducers/auth';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, ResponsiveEmbed, Dropdown, DropdownButton } from 'react-bootstrap';
import ConfirmNewHireKitModal from "../modals/ConfirmNewHireKitModal";
import NewHireKitOrderConfirmation from "./NewHireKitOrderConfirmation";
import KitItem from "../KitItem";
import { buildNHKChoiceGroups } from "../../utils/nhkChoiceGroups";

const AdminNewHireKit = () => {
    const dispatch = useDispatch();
    const catalogItem = useSelector(selectCurrentNewHireKitItems);
    const inventory = useSelector(selectInventory);
    const itemStatus = useSelector(state => state.catalog.adminItemStatus);
    const { isSuperAdmin, isSiteAdmin } = useSelector(state => state.auth);
    const [allSelectedSizes, setAllSelectedSizes] = useState([]);
    const [productList, setProductList] = useState([]); // choice groups
    const [choiceGroups, setChoiceGroups] = useState([]);
    const [newChoiceGroupTitle, setNewChoiceGroupTitle] = useState("");
    const [height, setHeight] = useState(0);
    const [newAddress, setNewAddress] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [orderItems, setOrderItems] = useState([]);
    const [polybagChecked, setPolybagChecked] = useState(false);
    const [polybagLabel, setPolybagLabel] = useState("");
    const [completedOrder, setCompletedOrder] = useState(null);
    const [completedOrderSnapshot, setCompletedOrderSnapshot] = useState(null);
    const [useFixedPrice, setUseFixedPrice] = useState(false);
    const [fixedPrice, setFixedPrice] = useState("");
    const [savingSettings, setSavingSettings] = useState(false);

    // New hire kit management
    const [allKits, setAllKits] = useState([]);
    const [currentKit, setCurrentKit] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [newKitTitle, setNewKitTitle] = useState("");
    const [editKitTitle, setEditKitTitle] = useState("");
    const [loadingKits, setLoadingKits] = useState(true);

    const location = useLocation();
    let navigate = useNavigate();

    // Load all kits on mount
    useEffect(() => {
        loadAllKits();
    }, [location.key]);

    const loadAllKits = async () => {
        setLoadingKits(true);
        try {
            const kits = await CatalogService.getAllNewHireKits();
            setAllKits(kits);
            if (kits.length > 0 && !currentKit) {
                setCurrentKit(kits[0]);
            }
        } catch (err) {
            console.error("Error loading kits:", err);
        }
        setLoadingKits(false);
    };

    // Load kit items when current kit changes
    useEffect(() => {
        if (currentKit) {
            dispatch(getNewHireKitItems(currentKit.id));
            fetchSettings();
            loadChoiceGroups(currentKit.id);
        }
    }, [currentKit, dispatch]);

    const loadChoiceGroups = async (kitId) => {
        try {
            const groups = await CatalogService.getNHKChoiceGroups(kitId);
            setChoiceGroups(Array.isArray(groups) ? groups : []);
        } catch (err) {
            console.error("Error loading choice groups:", err);
            setChoiceGroups([]);
        }
    };

    const handleCreateChoiceGroup = async () => {
        if (!currentKit || !newChoiceGroupTitle.trim()) return;
        try {
            await CatalogService.createNHKChoiceGroup(currentKit.id, newChoiceGroupTitle.trim());
            setNewChoiceGroupTitle("");
            await loadChoiceGroups(currentKit.id);
        } catch (err) {
            console.error("Error creating choice group:", err);
        }
    };

    const handleRenameChoiceGroup = async (group) => {
        const title = window.prompt("Choice group name", group.title);
        if (!title || !title.trim()) return;
        try {
            await CatalogService.updateNHKChoiceGroup(group.id, { title: title.trim() });
            await loadChoiceGroups(currentKit.id);
            dispatch(getNewHireKitItems(currentKit.id));
        } catch (err) {
            console.error("Error renaming choice group:", err);
        }
    };

    const handleDeleteChoiceGroup = async (group) => {
        if (!window.confirm(`Delete choice group "${group.title}"? Products in it become standalone.`)) return;
        try {
            await CatalogService.deleteNHKChoiceGroup(group.id);
            await loadChoiceGroups(currentKit.id);
            dispatch(getNewHireKitItems(currentKit.id));
        } catch (err) {
            console.error("Error deleting choice group:", err);
        }
    };

    const handleAssignChoiceGroup = async (nhkiId, choiceGroupId) => {
        try {
            await CatalogService.setNHKItemChoiceGroup(nhkiId, choiceGroupId);
            dispatch(getNewHireKitItems(currentKit.id));
        } catch (err) {
            console.error("Error assigning choice group:", err);
        }
    };

    const fetchSettings = async () => {
        if (!currentKit) return;
        try {
            const settings = await CatalogService.getNewHireKitSettings(currentKit.id);
            setUseFixedPrice(settings.useFixedPrice || false);
            setFixedPrice(settings.fixedPrice || "");
        } catch (err) {
            console.error("Error fetching NHK settings:", err);
        }
    };

    const saveSettings = async () => {
        if (!currentKit) return;
        setSavingSettings(true);
        try {
            await CatalogService.updateNewHireKitSettings(currentKit.id, useFixedPrice, useFixedPrice ? fixedPrice : null);
        } catch (err) {
            console.error("Error saving NHK settings:", err);
        }
        setSavingSettings(false);
    };

    // Kit management functions
    const handleCreateKit = async () => {
        if (!newKitTitle.trim()) return;
        try {
            const newKit = await CatalogService.createNewHireKit(newKitTitle.trim());
            setAllKits([...allKits, newKit]);
            setCurrentKit(newKit);
            setNewKitTitle("");
            setShowCreateModal(false);
        } catch (err) {
            console.error("Error creating kit:", err);
        }
    };

    const handleUpdateKit = async () => {
        if (!editKitTitle.trim() || !currentKit) return;
        try {
            await CatalogService.updateNewHireKit(currentKit.id, editKitTitle.trim());
            const updatedKits = allKits.map(k =>
                k.id === currentKit.id ? { ...k, title: editKitTitle.trim() } : k
            );
            setAllKits(updatedKits);
            setCurrentKit({ ...currentKit, title: editKitTitle.trim() });
            setShowEditModal(false);
        } catch (err) {
            console.error("Error updating kit:", err);
        }
    };

    const handleDeleteKit = async () => {
        if (!currentKit) return;
        try {
            await CatalogService.deleteNewHireKit(currentKit.id);
            const remainingKits = allKits.filter(k => k.id !== currentKit.id);
            setAllKits(remainingKits);
            setCurrentKit(remainingKits.length > 0 ? remainingKits[0] : null);
            setShowDeleteModal(false);
            setProductList([]);
        } catch (err) {
            console.error("Error deleting kit:", err);
        }
    };


    useEffect(() => {
        if (itemStatus === 'idle' && currentKit) {
            dispatch(getNewHireKitItems(currentKit.id));
        } else if (itemStatus === 'succeeded') {
            if (catalogItem && catalogItem.items && catalogItem.items.length > 0) {
                setProductList(buildNHKChoiceGroups(catalogItem.items));
            } else {
                setProductList([]);
            }
        }
    }, [itemStatus, dispatch, catalogItem, currentKit]);

    const setSize = (itemIdx, productId) => {
        console.log("SET SIZE", itemIdx, productId, allSelectedSizes);
        let newSelectedSizes = [];
        if (!allSelectedSizes || allSelectedSizes.length === 0) {
            newSelectedSizes = new Array(productList.length);
        } else {
            for (var i = 0; i < productList.length; i++) {
                newSelectedSizes[i] = allSelectedSizes[i];
            }
        }

        if (productId == "" || productId == null) {
            newSelectedSizes[itemIdx] = null;
        } else {
            newSelectedSizes[itemIdx] = productId;
        }

        setAllSelectedSizes([...newSelectedSizes]);
        setErrorMessage(null);
        console.log("After SET SIZE", itemIdx, productId, newSelectedSizes, allSelectedSizes);
    }

    const remove = async (item) => {
        console.log("REMOVING ITEM", item);
        await CatalogService.removeNewHireKitItem(item.nhkiId);
        if (currentKit) {
            dispatch(getNewHireKitItems(currentKit.id));
        }
    }

    let content = "";

    if (loadingKits) {
        content = <Spinner animation="border" />;
    } else if (!currentKit) {
        content = (
            <div>
                <p>No kits found. Create a new hire kit to get started.</p>
                <Button onClick={() => setShowCreateModal(true)}>Create New Kit</Button>
            </div>
        );
    } else if (itemStatus === 'loading') {
        content = <Spinner animation="border" />;
    } else if (itemStatus === 'succeeded') {
        if (productList && productList.length > 0) {
            console.log("PRODUCT LIST", productList);
            content = (
                <Container>
                    {productList.map((group, i) => (
                        <KitItem
                            key={group.id || i}
                            sizeIdx={i}
                            options={group.options}
                            inventory={inventory}
                            setSize={setSize}
                            remove={remove}
                            edit={true}
                            clearErrors={() => setErrorMessage(null)}
                            choiceGroups={choiceGroups}
                            onAssignChoiceGroup={handleAssignChoiceGroup}
                            groupTitle={group.isSolo ? null : group.title}
                        />
                    ))}
                </Container>
            );
        } else {
            content = <div>No items found. Go to a Product and Add to New Hire Kit</div>
        }

    } else if (itemStatus === 'failed') {
        content = <div>error</div>
    }

    const toggleExpansion = () => {
        console.log("TOGGLE EXPANSION", isExpanded);
        var expanded = isExpanded;
        setIsExpanded(!isExpanded);
        setHeight(expanded ? 0 : 'auto');
    };


    const submitNewHireKit = async () => {
        setSavingOrder(true);
        var newOrderItems = [];
        console.log("SUBMITTING NEW HIRE KIT", allSelectedSizes);
        var error = false;
        var errorMessage = "";

        // Check if all sizes are selected
        if (allSelectedSizes == null || allSelectedSizes.length == 0 || allSelectedSizes.length != productList.length) {
            errorMessage = "Please select a size for all items. ";
            error = true;
        }
        for (var j = 0; j < allSelectedSizes.length; j++) {
            if (allSelectedSizes[j] == null) {
                errorMessage = "Please select a size for all items. ";
                error = true;
                break;
            }
        }

        if (newAddress == null || newAddress.name == null) {
            errorMessage += "Please add a recipient address. ";
            error = true;
        }

        if(polybagChecked) {
            if(polybagLabel == null || polybagLabel == "") {
                errorMessage += "Please add a polybag label. ";
                error = true;
            }
        }

        if (error) {
            setErrorMessage(errorMessage);
            setSavingOrder(false);
            return;
        }

        console.log("ADDING ITEMS");
        for (var j = 0; j < allSelectedSizes.length; j++) {
            var selectedSizeProductId = allSelectedSizes[j];
            console.log("SELECTED SIZE PRODUCT ID", productList[j]);
            var selectedCatalogItem = null;
            for (var i = 0; i < catalogItem.items.length; i++) {
                if (catalogItem.items[i].sizes.find(size => size.productId == selectedSizeProductId)) {
                    selectedCatalogItem = catalogItem.items[i];
                    break;
                }
            }
            var selectedSize = selectedCatalogItem.sizes.find(size => size.productId == selectedSizeProductId);
            var item = {
                productId: selectedSizeProductId,
                decorations: selectedCatalogItem.logos,
                item: selectedCatalogItem,
                selectedSize: selectedSize.sizeName
            };
            console.log("ADDING ITEM", item);
            newOrderItems.push(item);
        }
        setOrderItems(newOrderItems);
        setShowConfirmation(true);
    }

    const confirm = async () => {
        try {
            setSavingOrder(true);
            const order = await OrderService.orderNewHireKit(
                orderItems,
                newAddress,
                polybagChecked,
                polybagLabel,
                currentKit?.id
            );
            setCompletedOrderSnapshot({
                items: orderItems,
                address: newAddress,
                polybagChecked,
                polybagLabel
            });
            setCompletedOrder(order);
            setShowConfirmation(false);
            setNewAddress(null);
            setAllSelectedSizes([]);
            setOrderItems([]);
            setPolybagChecked(false);
            setPolybagLabel("");
        } catch (err) {
            console.error(err);
            setErrorMessage(err?.response?.data?.message || err.message || "Failed to place New Hire Kit order.");
            setShowConfirmation(false);
        } finally {
            setSavingOrder(false);
        }
    };

    const startAnotherOrder = () => {
        setCompletedOrder(null);
        setCompletedOrderSnapshot(null);
        setErrorMessage(null);
    };

    const closeAddressModal = async (update, newAddress) => {
        toggleExpansion();
        if (update) {
            setNewAddress(newAddress);
            setErrorMessage(null);
        }
    }

    return (
        <Container>
            {completedOrder && completedOrderSnapshot && (
                <NewHireKitOrderConfirmation
                    order={completedOrder}
                    items={completedOrderSnapshot.items}
                    address={completedOrderSnapshot.address}
                    polybagChecked={completedOrderSnapshot.polybagChecked}
                    polybagLabel={completedOrderSnapshot.polybagLabel}
                    onDone={startAnotherOrder}
                />
            )}

            {!completedOrder && (
            <>
            {/* Kit Selection and Management */}
            <Row className="mb-3 align-items-center">
                <Col xs="auto">
                    <h2>New Hire Kit Order</h2>
                </Col>
                <Col xs="auto">
                    <DropdownButton
                        id="kit-selector"
                        title={currentKit ? currentKit.title : "Select Kit"}
                        variant="outline-primary"
                    >
                        {allKits.map(kit => (
                            <Dropdown.Item
                                key={kit.id}
                                onClick={() => setCurrentKit(kit)}
                                active={currentKit?.id === kit.id}
                            >
                                {kit.title}
                            </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => setShowCreateModal(true)}>
                            + Create New Kit
                        </Dropdown.Item>
                    </DropdownButton>
                </Col>
                {(isSuperAdmin || isSiteAdmin) && currentKit && (
                    <Col xs="auto">
                        <ButtonGroup>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                    setEditKitTitle(currentKit.title);
                                    setShowEditModal(true);
                                }}
                            >
                                Rename
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                Delete
                            </Button>
                        </ButtonGroup>
                    </Col>
                )}
            </Row>
            <hr />

            {(isSuperAdmin || isSiteAdmin) && currentKit && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Choice Groups</div>
                <p style={{ fontSize: '0.9em', color: '#666', marginBottom: 10 }}>
                  Create a group, then assign products below so users must pick <strong>one</strong> product from that group (then color and size). Unassigned products stay required on their own.
                </p>
                <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
                  <Form.Group style={{ minWidth: 220 }}>
                    <Form.Label style={{ fontSize: '0.85em' }}>New group name</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="e.g. Tops, Bottoms"
                      value={newChoiceGroupTitle}
                      onChange={(e) => setNewChoiceGroupTitle(e.target.value)}
                    />
                  </Form.Group>
                  <Button size="sm" variant="primary" disabled={!newChoiceGroupTitle.trim()} onClick={handleCreateChoiceGroup}>
                    Add Group
                  </Button>
                </div>
                {choiceGroups.length > 0 && (
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {choiceGroups.map((g) => (
                      <li key={g.id} style={{ marginBottom: 4 }}>
                        <strong>{g.title}</strong>
                        <Button size="sm" variant="link" onClick={() => handleRenameChoiceGroup(g)}>Rename</Button>
                        <Button size="sm" variant="link" className="text-danger" onClick={() => handleDeleteChoiceGroup(g)}>Delete</Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Create Kit Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Kit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Kit Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter kit name (e.g., Male, Female, Manager)"
                            value={newKitTitle}
                            onChange={(e) => setNewKitTitle(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateKit} disabled={!newKitTitle.trim()}>
                        Create Kit
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Kit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Rename Kit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Kit Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={editKitTitle}
                            onChange={(e) => setEditKitTitle(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateKit} disabled={!editKitTitle.trim()}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Kit Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Kit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete "{currentKit?.title}"? This will also remove all items from this kit.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteKit}>
                        Delete Kit
                    </Button>
                </Modal.Footer>
            </Modal>

            {currentKit && (
                <>
                    {isSuperAdmin && (
                        <>
                            <h4>Kit Pricing</h4>
                            <div style={{ marginLeft: 30, marginBottom: 20 }}>
                                <Form.Check
                                    type="switch"
                                    id="useFixedPrice"
                                    label="Use fixed price for entire kit"
                                    checked={useFixedPrice}
                                    onChange={(e) => setUseFixedPrice(e.target.checked)}
                                />
                                {useFixedPrice && (
                                    <Form.Group controlId="fixedPriceInput" style={{ marginTop: 10, maxWidth: 200 }}>
                                        <Form.Label>Fixed Kit Price ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="Enter fixed price"
                                            value={fixedPrice}
                                            onChange={(e) => setFixedPrice(e.target.value)}
                                        />
                                    </Form.Group>
                                )}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    style={{ marginTop: 10 }}
                                    onClick={saveSettings}
                                    disabled={savingSettings}
                                >
                                    {savingSettings ? "Saving..." : "Save Pricing Settings"}
                                </Button>
                            </div>
                            <hr />
                        </>
                    )}
                    <h4>Recipient</h4>
                    <div style={{ marginLeft: 30 }}>
                        <AnimateHeight duration={500} height={height == 0 ? 'auto' : 0}>
                            {newAddress && (
                                <div>
                                    <h2>Delivering to {newAddress.name}</h2>
                                    <p style={{ marginLeft: 30, padding: 5, border: "1px solid #CCCCCC" }}>{newAddress.street}<br />
                                        {newAddress.street2 != null && newAddress.street2 != "" && <>{newAddress.street2}<br /></>}
                                        {newAddress.city}, {newAddress.state} {newAddress.zip}<br />
                                    </p>
                                </div>)}
                            <div><Button onClick={toggleExpansion}>Set Address</Button></div>
                        </AnimateHeight>
                        <AnimateHeight duration={500} height={height}>
                            <ChooseAddress close={closeAddressModal} />
                        </AnimateHeight>

                        <Form.Group controlId="polybagCheckbox" style={{ marginTop: 20 }}>
                            <Form.Check
                                type="checkbox"
                                label="Include polybag packaging and label"
                                onChange={(e) => setPolybagChecked(e.target.checked)}
                            />
                        </Form.Group>
                        {polybagChecked && (
                            <Form.Group controlId="polybagLabelInput" style={{ marginTop: 10 }}>
                                <Form.Label>Polybag Label</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter polybag label"
                                    onChange={(e) => setPolybagLabel(e.target.value)}
                                />
                            </Form.Group>
                        )}
                    </div>
                    <hr />
                    {content}
                    <hr />
                    <Button onClick={submitNewHireKit} variant="primary" style={{ marginTop: 20 }}>
                        Confirm New Hire Kit
                    </Button>
                    {errorMessage && <div className="error">{errorMessage}</div>}
                    {showConfirmation && orderItems && <ConfirmNewHireKitModal show={showConfirmation} close={() => setShowConfirmation(false)} confirm={confirm} items={orderItems} address={newAddress} polybagChecked={polybagChecked} polybagLabel={polybagLabel}/>}
                </>
            )}
            </>
            )}
        </Container >
    );
};
export default AdminNewHireKit;